import { array, nativeEnum, number, object, string, z, ZodIssueCode } from 'zod';

import { makeZodValidate as makeValidate } from '@occc-app/design-system';
import {
  IS_INTEGER,
  IS_POSITIVE,
  IS_REQUIRED,
  IS_URL,
  isGreaterThan,
  isLessThan,
  isStringLengthUpTo,
} from '@occc-app/lib/constants/validationMessages';
import { Random_Award_Type } from '@occc/graphql';
import {
  CustomDataSchema,
  OfferFulfillmentPrioritySchema,
  OfferNameAndDescriptionSchema,
  withIsEnabled,
} from '@occc/validation';
import { fulfillmentWindowSchema } from '@occc/feat-constraints/schemas';
import { LimitDisabledSchema, LimitEnabledSchema, LimitTimePeriod } from '@occc/feat-limits';

import { GameType, SegmentIcon } from './types';
import { MIN_SEGMENTS_LENGTH, NO_AWARD_SEGMENT_ERROR } from './GameConfigureForm/constants';
import { hasAwardSegment } from './utils';

export const GlobalAwardCountSchema = object({
  id: string().optional(),
  limit: number().positive(IS_POSITIVE).optional(),
  timePeriod: nativeEnum(LimitTimePeriod, { required_error: IS_REQUIRED }),
}).superRefine((limit, ctx) => {
  if (limit.limit == null) {
    ctx.addIssue({ code: ZodIssueCode.custom, path: ['limit'], message: IS_REQUIRED });
  }
});

const MembershipAwardCountSchema = withIsEnabled(
  LimitEnabledSchema.extend({
    limit: number({ required_error: IS_REQUIRED }).positive(IS_POSITIVE),
  }),
  LimitDisabledSchema,
);

export const AwardCountSchema = object({
  membership: MembershipAwardCountSchema,
  global: GlobalAwardCountSchema,
});

export const SegmentSchema = object({
  id: string({ required_error: IS_REQUIRED }),
  type: nativeEnum(Random_Award_Type, { required_error: IS_REQUIRED }),
  limit: number({ required_error: IS_REQUIRED }).int(IS_INTEGER).min(1, isGreaterThan(0)),
  color: string().nullable(),
  icon: nativeEnum(SegmentIcon).nullable(),
  awardPoints: number().int(IS_INTEGER).min(1, isGreaterThan(0)).nullable(),
  awardOffer: object({ id: string(), name: string() }).nullable(),
  awardOfferExpirationDays: number().int(IS_INTEGER).min(1, isGreaterThan(1)).nullable(),
  awardOfferVoucherCount: number()
    .int(IS_INTEGER)
    .min(1, isGreaterThan(0))
    .max(100, isLessThan(101))
    .nullable(),
}).superRefine((segment, ctx) => {
  const requiredForAwardType: Partial<Record<Random_Award_Type, (keyof typeof segment)[]>> = {
    [Random_Award_Type.Points]: ['awardPoints'],
    [Random_Award_Type.Vouchers]: ['icon', 'awardOffer', 'awardOfferVoucherCount'],
  };

  requiredForAwardType[segment.type]?.forEach((field) => {
    if (segment[field] == null) {
      ctx.addIssue({ code: ZodIssueCode.custom, path: [field], message: IS_REQUIRED });
    }
  });
});

export const SpinWheelGameConfigSchema = object({
  headerText: string().max(35, isStringLengthUpTo(35)).nullable(),
  headerTextColor: string().nullable(),
  footerTextColor: string().nullable(),
  borderColor: string().nullable(),
  backgroundPrimaryColor: string().nullable(),
  backgroundSecondaryColor: string().nullable(),
  backgroundImage: string().url(IS_URL).nullable(),
  foregroundImage: string().url(IS_URL).nullable(),
  buttonColor: string().nullable(),
  buttonTextColor: string().nullable(),
});

export const GameConfigurationSchema = OfferNameAndDescriptionSchema.merge(
  OfferFulfillmentPrioritySchema,
)
  .extend({
    gameType: nativeEnum(GameType, { required_error: IS_REQUIRED }),
    segments: array(SegmentSchema).min(
      MIN_SEGMENTS_LENGTH,
      `Add at least ${MIN_SEGMENTS_LENGTH} segments`,
    ),
    purchasableOffer: object({ id: string(), name: string() }).nullable(),
    spinWheelGameConfig: SpinWheelGameConfigSchema.nullable(),
    fulfillmentWindow: fulfillmentWindowSchema,
    awardCount: AwardCountSchema,
    customData: CustomDataSchema,
  })
  .superRefine((values, ctx) => {
    if (!hasAwardSegment(values.segments)) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        path: ['segments'],
        message: NO_AWARD_SEGMENT_ERROR,
      });
    }
  });

export const validateConfiguration = makeValidate(GameConfigurationSchema);

export const NativeGamePaletteVariantSchema = z.enum([
  'coco',
  'cefco',
  'delek',
  'mpc',
  'gulf',
  'yesway',
  'raceway',
  'gkraceway',
  'egamerica',
  'stinker',
  'rofo',
  'jacksons',
  'refuel',
  'parkers',
]);
